/**
 * adaptiveSuggestionEngine — Pure deterministic rule engine for adaptive training suggestions.
 *
 * Evaluates existing signals (fatigue, pain, sleep, missed sessions, streaks)
 * and returns contextual suggestions. No AI, no DB writes, no program mutations.
 *
 * Rules are ordered by severity (highest first).
 * Consumers filter by location and dismiss state.
 *
 * @since Adaptive Suggestion Layer v1
 */

// ── Context (all values already available in Dashboard) ──

export interface AdaptiveContext {
  /** At least one planned session was missed yesterday */
  missedYesterday: boolean;
  /** Today has a planned training session */
  todayHasSession: boolean;
  /** Current fatigue score (0–10), null if unavailable */
  fatigueScore: number | null;
  /** Number of active pain flags today */
  painCount: number;
  /** Consecutive days with training_done */
  consecutiveTrainingDays: number;
  /** Last recorded sleep in hours, null if unavailable */
  sleepLast: number | null;
  /** Number of missed sessions this week (past days only) */
  missedSessionDays: number;
  /** How far through the week we are (0 = Monday, 6 = Sunday) */
  weekDayIndex: number;
}

// ── Suggestion ──

export type SuggestionSeverity = "info" | "warning" | "caution";

export interface AdaptiveSuggestion {
  /** Unique rule ID — used for dismiss persistence */
  id: string;
  /** User-facing message */
  message: string;
  /** Visual emoji cue */
  emoji: string;
  /** Severity drives border color */
  severity: SuggestionSeverity;
  /** Optional CTA label */
  actionLabel?: string;
  /** Optional navigation route */
  actionRoute?: string;
}

// ── Rule definitions ──

interface AdaptiveRule {
  id: string;
  check: (ctx: AdaptiveContext) => boolean;
  suggestion: Omit<AdaptiveSuggestion, "id">;
}

const RULES: AdaptiveRule[] = [
  {
    id: "pain_flag_caution",
    check: (ctx) => ctx.painCount >= 1 && ctx.todayHasSession,
    suggestion: {
      message: "Une douleur est signalée. Adapte ou remplace ta séance si besoin.",
      emoji: "⚠️",
      severity: "warning",
      actionLabel: "Voir ma séance",
      actionRoute: "/sessions",
    },
  },
  {
    id: "high_fatigue_warning",
    check: (ctx) => (ctx.fatigueScore ?? 0) >= 7 && ctx.todayHasSession,
    suggestion: {
      message: "Ton score de fatigue est élevé. Tu peux alléger ou reporter ta séance.",
      emoji: "🔋",
      severity: "warning",
    },
  },
  {
    id: "sleep_deficit",
    check: (ctx) => ctx.sleepLast !== null && ctx.sleepLast < 6 && ctx.todayHasSession,
    suggestion: {
      message: "Nuit courte. Réduis l'intensité ou reporte ta séance si besoin.",
      emoji: "😴",
      severity: "caution",
    },
  },
  {
    id: "overtraining_streak",
    check: (ctx) => ctx.consecutiveTrainingDays >= 5,
    suggestion: {
      message: "5 jours consécutifs d'entraînement. Pense à intégrer une journée de récupération.",
      emoji: "🛑",
      severity: "warning",
    },
  },
  {
    id: "missed_session_carry",
    check: (ctx) => ctx.missedYesterday && !ctx.todayHasSession,
    suggestion: {
      message: "Tu as manqué ta séance d'hier. Tu peux la déplacer à aujourd'hui.",
      emoji: "↩️",
      severity: "info",
      actionLabel: "Voir le planning",
      actionRoute: "/planning",
    },
  },
  {
    id: "week_behind",
    check: (ctx) => ctx.missedSessionDays >= 2 && ctx.weekDayIndex >= 3,
    suggestion: {
      message: "Plusieurs séances manquées cette semaine. Tu peux réorganiser ton planning.",
      emoji: "📅",
      severity: "info",
      actionLabel: "Réorganiser",
      actionRoute: "/planning",
    },
  },
];

// ── Engine ──

/**
 * Evaluate all rules against the current context.
 * Returns suggestions ordered by severity (warning > caution > info).
 * Maximum 2 suggestions to avoid banner spam.
 */
export function computeAdaptiveSuggestions(ctx: AdaptiveContext): AdaptiveSuggestion[] {
  const matched: AdaptiveSuggestion[] = [];

  for (const rule of RULES) {
    if (rule.check(ctx)) {
      matched.push({ id: rule.id, ...rule.suggestion });
    }
  }

  return matched.slice(0, 2);
}
